import {
  fetchOperationById,
  fetchOperationsByExternalAwsAccountId,
  selectLatestSyncOpByExternalAwsAccountId,
} from "@app/deploy";
import { useDispatch, useSelector } from "@app/react";
import type { DeployOperation } from "@app/types";
import { useCallback, useEffect, useMemo } from "react";
import { useInterval } from "./use-interval";
import { useVisibility } from "./use-visibility";

const ACTIVE_OP_STATUSES = new Set(["queued", "running"]);

export function useExternalAwsAccountSyncOpsPoller(accountIds: string[]) {
  const dispatch = useDispatch();
  const isTabActive = useVisibility();
  const ids = useMemo(() => accountIds.filter(Boolean), [accountIds]);
  const ops = useSelector((s) =>
    ids.map((id) => selectLatestSyncOpByExternalAwsAccountId(s, { id })),
  ) as DeployOperation[];
  const activeSyncOpIds = ops
    .filter((op) => ACTIVE_OP_STATUSES.has(op.status))
    .map((op) => op.id)
    .filter(Boolean);
  const hasActiveSync = activeSyncOpIds.length > 0;

  const pollSyncOps = useCallback(() => {
    if (ids.length === 0) {
      return;
    }
    ids.forEach((id) => {
      dispatch(
        fetchOperationsByExternalAwsAccountId({
          id,
          page: 1,
          resourceType: "ExternalAwsAccount",
          operationType: "sync",
          operationStatus: "",
          startDate: "",
          endDate: "",
        }),
      );
    });
  }, [dispatch, ids]);

  useEffect(() => {
    if (!isTabActive) {
      return;
    }
    pollSyncOps();
  }, [isTabActive, pollSyncOps]);

  useInterval(
    () => {
      if (!isTabActive) {
        return;
      }
      pollSyncOps();
    },
    isTabActive && ids.length > 0 ? (hasActiveSync ? 1000 : 10 * 1000) : null,
  );

  useInterval(
    () => {
      if (!isTabActive || activeSyncOpIds.length === 0) {
        return;
      }
      activeSyncOpIds.forEach((opId) => {
        dispatch(fetchOperationById({ id: opId }));
      });
    },
    isTabActive && activeSyncOpIds.length > 0 ? 1000 : null,
  );

  return { hasActiveSync, isTabActive };
}
